"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

type DialogSize = "confirm" | "standard" | "workspace" | "canvas" | "full"
type DialogPadding = "none" | "sm" | "md"
type DialogTone = "default" | "brand" | "info" | "success" | "warning" | "danger"

const SIZE_MAP: Record<DialogSize, string> = {
  confirm: "sm:max-w-md",
  standard: "sm:max-w-lg",
  workspace: "sm:max-w-3xl",
  canvas: "sm:max-w-[60rem]",
  full: "sm:max-w-none sm:w-full",
}

const PADDING_MAP: Record<DialogPadding, string> = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
}

const BAND_COLOR_MAP: Record<Exclude<DialogTone, "default">, string> = {
  brand: "bg-brand-red",
  info: "bg-sky-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
      className={cn(
        "fixed inset-0 z-[900] bg-black/65 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: DialogSize
  padding?: DialogPadding
  tone?: DialogTone
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      size = "standard",
      padding = "md",
      tone = "brand",
      ...props
    },
    ref
  ) => {
    const toneClasses =
      tone !== "default"
        ? cn(
            "before:absolute before:left-0 before:top-0 before:h-1 before:w-full before:rounded-t-[inherit] before:content-['']",
            `before:${BAND_COLOR_MAP[tone as Exclude<DialogTone, "default">]}`
          )
        : null

    return (
      <DialogPortal>
        <DialogOverlay />
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6">
          <DialogPrimitive.Content
            ref={ref}
            aria-describedby={undefined}
            className={cn(
              "relative w-full max-h-[86vh] overflow-hidden rounded-none border border-border/45 bg-bg-0 shadow-[0_32px_120px_rgba(15,23,42,0.32)] outline-none ring-0 transition-all duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              SIZE_MAP[size],
              PADDING_MAP[padding],
              "grid gap-5",
              toneClasses,
              className
            )}
            {...props}
          >
            {children}
            <DialogPrimitive.Close className="absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-bg-0/85 text-text-400 shadow-sm transition-colors hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    )
  }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 border-t border-border/60 bg-bg-0 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

type ModalFooterTone = "default" | "destructive"

const FOOTER_SPACING_MAP: Record<DialogPadding, string> = {
  none: "mt-4 pt-4",
  sm: "-mx-4 mt-4 px-4 pt-4",
  md: "-mx-6 mt-6 px-6 pt-5",
}

interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  primaryAction: React.ReactNode
  secondaryActions?: React.ReactNode[]
  supportingText?: React.ReactNode
  spacing?: DialogPadding
  tone?: ModalFooterTone
}

const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(
  (
    {
      className,
      primaryAction,
      secondaryActions = [],
      supportingText,
      spacing = "md",
      tone = "default",
      ...props
    },
    ref
  ) => {
    if (process.env.NODE_ENV !== "production" && !primaryAction) {
      console.warn("ModalFooter: a primaryAction is required for accessibility and layout.")
    }

    const normalizedSecondary = React.Children.toArray(secondaryActions)

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex flex-col gap-3 sm:gap-4",
          FOOTER_SPACING_MAP[spacing],
          tone === "destructive"
            ? "border-t border-brand-red/30 bg-bg-rose before:absolute before:left-0 before:top-0 before:h-1 before:w-full before:bg-brand-red before:content-['']"
            : "border-t border-border/60",
          className
        )}
        {...props}
      >
        <div
          className={cn(
            "flex flex-col gap-3 sm:flex-row sm:items-center",
            supportingText ? "sm:justify-between" : "sm:justify-end"
          )}
        >
          {supportingText && (
            <div className="text-xs text-text-500 sm:max-w-md">{supportingText}</div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
            {normalizedSecondary.map((action, index) => (
              <div key={`secondary-${index}`} className="shrink-0">
                {action}
              </div>
            ))}
            {primaryAction && (
              <div className="order-last shrink-0 sm:order-last">
                {primaryAction}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)
ModalFooter.displayName = "ModalFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-[13px] leading-relaxed text-text-500", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

type ModalHeaderTone = "neutral" | "brand"

const HEADER_TONE_MAP: Record<ModalHeaderTone, string> = {
  neutral: "bg-bg-0 text-text-900",
  brand: "bg-rose-surface text-text-900",
}

const HEADER_SPACING_MAP: Record<DialogPadding, string> = {
  none: "px-0 pt-4 pb-3",
  sm: "-mx-4 -mt-4 px-4 pt-4 pb-3",
  md: "-mx-6 -mt-6 px-6 pt-6 pb-4",
}

const BAND_SPACING_MAP: Record<DialogPadding, string> = {
  none: "mx-0",
  sm: "-mx-4 -mt-4",
  md: "-mx-6 -mt-6",
}

interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: ModalHeaderTone
  spacing?: DialogPadding
  divider?: boolean
}

const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  (
    {
      className,
      tone = "brand",
      spacing = "md",
      divider = true,
      ...props
    },
    ref
  ) => {
    const dividerClass =
      divider && tone === "brand"
        ? "border-b border-brand-red/30"
        : divider
          ? "border-b border-border/60"
          : ""

    return (
      <div
        ref={ref}
        className={cn(
          "relative rounded-t-[inherit]",
          HEADER_TONE_MAP[tone],
          HEADER_SPACING_MAP[spacing],
          dividerClass,
          className
        )}
        {...props}
      />
    )
  }
)
ModalHeader.displayName = "ModalHeader"

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "centered" | "compact"
  tone?: ModalHeaderTone
  spacing?: DialogPadding
  divider?: boolean
}

const HEADER_VARIANT_MAP: Record<
  NonNullable<DialogHeaderProps["variant"]>,
  string
> = {
  default: "items-start text-left",
  centered: "items-center text-center",
  compact: "items-start text-left gap-1",
}

const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  (
    {
      className,
      variant = "default",
      tone = "brand",
      spacing = "md",
      divider = true,
      ...props
    },
    ref
  ) => (
    <ModalHeader
      ref={ref}
      tone={tone}
      spacing={spacing}
      divider={divider}
      className={cn("flex flex-col gap-2", HEADER_VARIANT_MAP[variant], className)}
      {...props}
    />
  )
)
DialogHeader.displayName = "DialogHeader"

const ModalSubtitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-text-500", className)}
    {...props}
  />
))
ModalSubtitle.displayName = "ModalSubtitle"

interface ModalBandProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: Exclude<DialogTone, "default">
  spacing?: DialogPadding
}

const ModalBand = React.forwardRef<HTMLDivElement, ModalBandProps>(
  ({ className, tone = "brand", spacing = "md", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "h-[3px] rounded-t-[inherit]",
        BAND_SPACING_MAP[spacing],
        BAND_COLOR_MAP[tone],
        className
      )}
      {...props}
    />
  )
)
ModalBand.displayName = "ModalBand"

const ModalLabel = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("text-xs font-medium uppercase tracking-wide text-text-700", className)}
    {...props}
  />
))
ModalLabel.displayName = "ModalLabel"

const ModalValue = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm font-semibold text-text-900", className)}
    {...props}
  />
))
ModalValue.displayName = "ModalValue"

const ModalHelperText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-xs text-text-500", className)}
    {...props}
  />
))
ModalHelperText.displayName = "ModalHelperText"

interface ModalPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: "sm" | "md"
}

const PANEL_PADDING_MAP: Record<NonNullable<ModalPanelProps["padding"]>, string> =
  {
    sm: "p-3",
    md: "p-4",
  }

const ModalPanel = React.forwardRef<HTMLDivElement, ModalPanelProps>(
  ({ className, padding = "md", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-border/60 bg-bg-1",
        PANEL_PADDING_MAP[padding],
        className
      )}
      {...props}
    />
  )
)
ModalPanel.displayName = "ModalPanel"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  ModalFooter,
  DialogTitle,
  DialogDescription,
  ModalHeader,
  ModalSubtitle,
  ModalBand,
  ModalLabel,
  ModalValue,
  ModalHelperText,
  ModalPanel,
}
